import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { useShallow } from 'zustand/react/shallow';

import { Button } from '@/components/buttons';
import { ImageComponent } from '@/components/Image';
import {
  ProgressBar,
  ValuePosition,
} from '@/components/progressBars/LinearProgressBar';
import { Skeleton } from '@/components/Skeleton';
import { useToggle } from '@/hooks/common';
import { useBonanzaPromo } from '@/modules/promotions';
import { useCommonStore } from '@/store';

import { ScBonanzaPromoSection } from './styled';
import { ProgressBarMode } from '@/components/progressBars/LinearProgressBar/styled';

export interface BonanzaPromoSectionProps {
  isMobile?: boolean;
  onLinkClick?: () => void;
}

export const BonanzaPromoSection: FC<BonanzaPromoSectionProps> = ({
  isMobile,
  onLinkClick,
}) => {
  const { t } = useTranslation();

  const [isFullInfoOpen, toggleFullInfo] = useToggle();
  const {
    images: {
      arrowDownGrayIcon: { component: ArrowDownGrayIcon },
    },
  } = useTheme();

  const {
    bonanzaReward,
    bonanzaPromoMenuData,
    bonanzaProgressPercent,
    pendingBonanzaPromoMenuData,
  } = useCommonStore(
    useShallow((state) => ({
      bonanzaReward: state.bonanzaReward,
      bonanzaPromoMenuData: state.bonanzaPromoMenuData,
      bonanzaProgressPercent: state.bonanzaProgressPercent,
      pendingBonanzaPromoMenuData: state.pendingBonanzaPromoMenuData,
    }))
  );

  const {
    isLockedPromo,
    isBonusIssued,
    characterByStatus,
    handleAcknowledgeBonus,
    handleNavigateToBonanza,
  } = useBonanzaPromo();

  const levelInfo = useMemo(() => {
    if (isLockedPromo)
      return (
        <div className="text">
          <span>
            <b>{bonanzaPromoMenuData?.menu.returnToStartFresh}</b>
          </span>
        </div>
      );

    return (
      <div className="text">
        <span>
          <b>{bonanzaPromoMenuData?.menu.yourProgress}:</b>
          {bonanzaProgressPercent}%
        </span>
        <span>
          <b>
            {isBonusIssued
              ? t('common:bonanzaPromo.bonusReceived')
              : bonanzaPromoMenuData?.menu.yourReward}
            :
          </b>
          {bonanzaReward}
        </span>
      </div>
    );
  }, [
    t,
    bonanzaPromoMenuData,
    bonanzaProgressPercent,
    bonanzaReward,
    isBonusIssued,
    isLockedPromo,
  ]);

  if (pendingBonanzaPromoMenuData)
    return <Skeleton.Rect height={isMobile ? 84 : 136} />;

  if (!characterByStatus || !bonanzaPromoMenuData) return null;

  return (
    <ScBonanzaPromoSection
      showFullInfo={isFullInfoOpen}
      onClick={() => isMobile && toggleFullInfo()}
    >
      <div className="info">
        <div className="level-info">
          {levelInfo}
          {isMobile && (
            <div className="addons">
              <ImageComponent
                src={characterByStatus.image.url}
                width={44}
                height={44}
                alt="logo"
                objectFit="contain"
              />
              <ArrowDownGrayIcon />
            </div>
          )}
        </div>
      </div>

      {!isLockedPromo && !isBonusIssued && (
        <ProgressBar
          value={bonanzaProgressPercent}
          mode={ProgressBarMode.PRIMARY_GRAY}
          showValue={ValuePosition.CENTER}
          height={24}
          borderRadius="24px"
          borderRadiusInner={'24px 0 0 24px'}
          withUnits
          showTo={false}
        />
      )}
      {isBonusIssued && (
        <Button primary wide small onClick={handleAcknowledgeBonus}>
          {t('common:gotIt')}
        </Button>
      )}

      {(!isMobile || isFullInfoOpen) && (
        <Button
          navigation
          onClick={() => {
            onLinkClick && onLinkClick();
            handleNavigateToBonanza();
          }}
        >
          {bonanzaPromoMenuData.pageTitle}
        </Button>
      )}
    </ScBonanzaPromoSection>
  );
};
